import apiClient from "../axios";

// ---- Standard field catalog (opt-in canonical view) ----
// Mirrors the backend's `StandardFieldResponseSchema` (standard-fields-routes.ts).
// The `id` is either a real UUID (existing StandardCustomField row) or
// `canonical:<entityType>:<fieldKey>` for core fields the org hasn't created yet.
export interface CatalogField {
  dataType: string;
  displayName: string;
  entityType: string;
  fieldKey: string;
  id: string; // real UUID *or* `canonical:<entityType>:<fieldKey>`
  isCanonical?: boolean; // true only for the synthetic stubs
  isDefault: boolean;
  isHardCoded: boolean;
  isPII: boolean;
}

// ---- Enums / literals (mirror backend Prisma enums + spec) ----
export type IngestionCrmType = "PUSH_JSON" | "SPREADSHEET_UPLOAD" | "SPREADSHEET_URL";
export type RecordStatus =
  | "FAILED"
  | "IGNORED"
  | "PENDING_JOIN"
  | "PENDING_ROUTING"
  | "PENDING_STRUCTURE"
  | "PROCESSED"
  | "ROUTED";
// Inbox queue identifiers as used by /inbox/approve-all (§2).
export type InboxQueue = "field-drift" | "routing" | "structure";
export type FieldTargetKind = "CREATE_STANDARD_FIELD" | "IGNORE" | "STANDARD_FIELD";
export type RoutingActionKind = "ASSIGN_EXISTING" | "CREATE_NEW" | "IGNORE";

// ---- Sources (SourceSummary, §2 `GET /sources` + `GET /summary`.sources) ----
export interface SourceSummary {
  caseCount: number; // distinct live cases from this source (deduped across re-pulls) — the "cases" figure
  createdAt: string; // when the source was added (CrmConfig.createdAt, ISO)
  crmConfigId: string; // CrmConfig.id — the canonical id, NOT `sourceId`
  crmType: IngestionCrmType;
  deletedAt: null | string;
  hasPrevToken: boolean; // tokenHmacPrev present (rotation window open)
  lastReceivedAt: null | string;
  mappedCampaignCount: number;
  name: string;
  organizationId: string;
  organizationName: string;
  presetKey?: string;
  recordCount: number; // cumulative records RECEIVED (append-per-payload) — NOT the case count
  urlPathSlug?: string;
}

// V1 presets: `{ key, label }` (§2 `GET /presets` → Generic, Litify).
export interface SourcePreset {
  key: string;
  label: string;
}

// A source's CONFIRMED case-type → campaign routing (config.routingConfig.mappings),
// joined with the campaign name. Pending routings come from the inbox queue separately.
export interface ConfirmedRouting {
  campaignId: string;
  campaignName: null | string;
  routingValue: string;
}

// A single confirmed structure entry: the stored RecordExtraction + the routingField
// that was approved alongside it (sourced from config.routingConfig.field).
export interface ConfirmedStructure {
  // The single-campaign destination, when the source routes its whole feed to one campaign
  // (no per-value routingField). Lets the edit UI show the real campaign instead of "(none)".
  defaultCampaignId?: string;
  externalIdPath: string;
  externalIdPaths?: string[];
  inherit?: Record<string, string>;
  recordsPath: string;
  routingField?: string;
}

// One row of a source's receive log (Overview "Ingestion history").
export interface IngestionHistoryItem {
  id: string;
  kind: "PUSH" | "UPLOAD" | "URL_PULL";
  processedAt: null | string;
  processingStatus: string;
  receivedAt: string;
  recordCount: number;
}

// Everything the "Re-run setup" wizard needs to pre-populate from an already-confirmed source:
// the confirmed structure (as a StructureDecision-shaped suggestion + a sample record), the
// single-campaign destination (bug E), and the full per-column field list (mapped + unmapped)
// so each column can be re-mapped. Sample/profile fields degrade to empty when no retained
// record is available (e.g. S3-less local dev, TTL-dropped rawRecord).
export interface SourceEditContext {
  columnProfiles: ColumnProfile[];
  defaultCampaignId: null | string;
  fields: FieldDecision[];
  // The source's currently-stored firm mappers, so the edit wizard pre-fills the firm section
  // and re-saves the same set (approve replaces config.orgMappers wholesale — without this a
  // re-run would silently drop the source's firm mapping).
  orgMappers: WizardOrgMapper[];
  // The CONFIRMED routing values (case-type values already mapped to campaigns). The edit
  // wizard shows their count as the routing-field impact ("→ N campaigns") — the true total,
  // where the ≤200-record sample undercounts. Empty when everything routes to one campaign.
  routingValues: string[];
  sampleRecord: unknown;
  structureSignature: null | string;
  suggestion: StructureDecision["suggestion"];
}

export interface CreateSourceRequest {
  authHeader?: string; // SPREADSHEET_URL only — outbound auth header, stored encrypted
  kind: IngestionCrmType; // §2 body field is `kind`
  name: string;
  organizationId: string;
  presetKey?: string; // omitted = "Generic"
  url?: string; // required for SPREADSHEET_URL — the remote feed URL the pull runner GETs
}

// Token returned exactly once — never re-fetchable.
export interface CreateSourceResponse {
  source: SourceSummary;
  token: null | string; // plaintext bearer, push/upload only; null for URL
}

// ---- Mission-control summary (§2 `GET /summary`) ----
export interface SummaryStats {
  campaigns: number;
  liveInCampaigns: number;
  thisWeek: number;
  totalIngested: number;
}
export interface IntakePoint {
  count: number;
  label: string;
}
export interface ActivityEvent {
  id: string;
  kind: "PUSH" | "UPLOAD" | "URL_PULL";
  processingStatus: string;
  receivedAt: string;
  recordCount: number;
  sourceName: string;
}
export interface ReviewItem {
  // c2 gate: "PENDING" while the background mapping suggester is still computing AI
  // suggestions for this source's routing/field decisions; "READY" once a verdict is
  // cached (or there are no AI-backed decisions to wait on). The Review UI blocks map/edit
  // until READY so the human never acts on a heuristic placeholder the AI will override.
  aiStatus: "PENDING" | "READY";
  campaignId: null | string; // null = "needs a campaign" (PENDING_ROUTING with no match)
  campaignName: null | string;
  crmConfigId: string;
  failureCount: number;
  fieldCount: number;
  headline: string; // e.g. "Depo Provera — 8 new fields to map"
  routingCount: number;
  sourceName: string;
  structureCount: number;
}
// §2 response: { stats, intakeSeries, recentActivity, reviewByCampaign, sources }.
export interface IngestionSummary {
  intakeSeries: IntakePoint[];
  recentActivity: ActivityEvent[];
  reviewByCampaign: ReviewItem[];
  sources: SourceSummary[];
  stats: SummaryStats;
}

// ---- Firms (handling/referring/marketing mapping for ingestion sources) ----
export type FirmRole = "HANDLING" | "MARKETING" | "REFERRING";
export interface FirmColumnSuggestion {
  column: string;
  confidence: number;
  matchedExistingFirms: number;
  reason: string;
  role: FirmRole;
}
// Firm mappers the wizard persists: a column carrying the firm name, or a fixed firm.
export type WizardOrgMapper =
  | { kind: "FIXED"; label?: string; name?: string; organizationId: string; role: FirmRole }
  | { kind: "NAME_ONLY"; label?: string; role: FirmRole; valuePath: string };
export interface Firm {
  id: string;
  name: string;
  orgType: string;
}
export interface FirmsResponse {
  firms: Firm[];
  ownerOrg: { id: string; name: string } | null;
}

// ---- Column profile (per-column statistics returned with StructureDecision) ----
export interface ColumnProfile {
  column: string;
  distinct: number;
  fillRate: number;
  nonEmpty: number;
  roleHint: string;
  total: number;
  uniqueness: number;
}

// ---- Per-sheet structure detection (returned by the upload route; reviewed in the wizard) ----
export interface SheetAnalysis {
  columns: string[];
  dataRowCount: number;
  dataStartRow: number;
  headerRow: number;
  hidden: boolean;
  looksLikeRollup: boolean;
  rollupReason?: string;
  sampleRows: Record<string, unknown>[];
  sheetName: string;
}

export interface WorkbookAnalysis {
  kind: "csv" | "xlsx";
  sheets: SheetAnalysis[];
}

/** One sheet's confirmed parse config, sent to the sheet-config endpoint. */
export interface SheetConfigInput {
  entityKind: "CASE" | "DOCUMENT" | "NOTE" | "SKIP";
  headerRow: number;
  skipRows?: number;
}

/** AI's opt-in second opinion on one sheet's layout (from suggestSheets). */
export interface SheetSuggestion {
  /** Best-fit campaign category for the tab (one of the categories we sent), when confident. */
  category?: string;
  confidence: number;
  dataStartRow: number;
  headerRow: number;
  kind: "FLAT" | "GROUPED" | "PIVOT";
  note?: string;
  sheetName: string;
}

// ---- Decisions (one type per queue) ----
export interface StructureDecision {
  columnProfiles: ColumnProfile[];
  crmConfigId: string;
  sampleRecord: unknown; // raw leaf object for SampleRecordView
  structureSignature: string;
  suggestion: {
    collisions: number;
    confidence: number;
    externalIdPath: string;
    externalIdPaths?: string[];
    // Detected firm columns (handling/referring/marketing) for prepopulating the firm mappers.
    firmSuggestions: FirmColumnSuggestion[];
    // Fan-out synthetic-key -> parent-path (AI verdict). Round-trips through approve so a
    // nested fan-out (e.g. campaigns[].matters routed by an inherited parent field) confirms
    // intact. Present only when an AI structure verdict proposed it.
    inherit?: Record<string, string>;
    // Rows whose ID value is entirely blank — they can't be imported with this key.
    missing: number;
    reason: string;
    // The RecordExtraction the backend round-trips on approve (§2 body `recordExtraction`).
    recordsPath: string;
    routingField?: string;
    // "ai" when an AI structure verdict was preferred, else "heuristic".
    source?: "ai" | "heuristic";
    uniqueness: number;
  };
  type: "STRUCTURE";
}

export interface FieldDecision {
  // False until the source's latest AI mapping verdict covers this column (it's computed in
  // the background after each receive); `suggestion` is heuristic-only until then and may
  // improve on a later poll (RAP-784).
  aiReady?: boolean;
  crmConfigId: string;
  sampleValues: unknown[];
  sourceField: string; // JSON path / column header
  suggestion: {
    confidence: number;
    // Per spec CREATE guard: existing-field match candidate, if any.
    existingMatch?: { fieldId: string; score: number; standardFieldName: string };
    reason: string;
    target: {
      fieldId?: string; // STANDARD_FIELD → existing StandardCustomField id (§2 body `target.fieldId`)
      kind: FieldTargetKind;
      proposedDisplayName?: string;
      proposedFieldKey?: string; // CREATE_STANDARD_FIELD
      proposedType?: string;
      standardFieldName?: string;
    };
  };
  type: "FIELD";
}

export interface RoutingDecision {
  crmConfigId: string;
  recordCount: number;
  routingValue: string; // case_type / sheet name / join target
  suggestion: {
    action: { campaignId?: string; campaignName?: string; kind: RoutingActionKind };
    confidence: number;
    reason: string;
  };
  type: "ROUTING";
}

export interface FailureItem {
  errorMessage: string;
  externalId: null | string;
  failedAt: string;
  payloadId: string;
  recordId: null | string; // null for whole-payload failure
  sourceName: string;
}

// §2 `GET /inbox/landing` rollup. Each queue is fetched separately; landing
// gives the counts (and `total`) for the badge + Review landing summary.
export interface InboxLanding {
  failures: number;
  fieldDrift: number;
  routing: number;
  structure: number;
  total: number;
}

// ---- Approve payloads (canonical §2 bodies) ----
// `recordExtraction` is the (possibly edited) extraction the human confirms.
export interface RecordExtraction {
  // Key-fields designations (the dashboard-driving core fields, chosen in the Key fields step):
  // the column carrying the case status/stage (→ Case.caseStage), or a constant stage applied to
  // every row when there's no such column.
  caseStageColumn?: string;
  // The column carrying the case open/intake date (→ Case.dateOpened).
  dateOpenedColumn?: string;
  // Constant stage applied to every row when caseStageColumn is unset.
  defaultCaseStage?: string;
  externalIdPath: string;
  externalIdPaths?: string[];
  // Fan-out synthetic-key -> parent-path. Passed through from the AI suggestion so a nested
  // fan-out structure confirms with its parent-inherited routing key intact.
  inherit?: Record<string, string>;
  recordsPath: string;
  routingField?: string;
  // When no dateOpenedColumn is chosen, default Case.dateOpened to the ingest time.
  useSyncTimeAsDateOpened?: boolean;
}
export interface ApproveStructureRequest {
  crmConfigId: string;
  // One-campaign mode: assign the whole source to this campaign. Every record routes to it
  // (status ROUTED) and the projection pipeline fires immediately. Mutually exclusive with
  // recordExtraction.routingField (which routes per-column-value instead).
  defaultCampaignId?: string;
  // Firm mappers (handling/referring/marketing): a column carrying the firm name, or a fixed
  // firm for the whole source. The pipeline turns these into per-case CaseOrg links.
  orgMappers?: WizardOrgMapper[];
  recordExtraction: RecordExtraction;
  structureSignature: string;
}
export interface ApproveFieldDriftRequest {
  crmConfigId: string;
  reproject?: boolean; // Flow A backfill prompt
  sourceField: string;
  // For CREATE_STANDARD_FIELD the new field is minted server-side from proposedDisplayName
  // + proposedType (the backend derives sensible defaults if omitted).
  target: {
    fieldId?: string;
    kind: FieldTargetKind;
    proposedDisplayName?: string;
    proposedType?: string;
  };
}
export interface ApproveRoutingRequest {
  action: { campaignId?: string; kind: RoutingActionKind };
  crmConfigId: string;
  routingValue: string;
}
export interface ApproveAllRequest {
  crmConfigId?: string; // optional — omit to act across all sources
  minConfidence?: number; // default 0.85
  queue: InboxQueue;
  /** When true and queue is "field-drift", backfill already-imported cases (Flow A). */
  reproject?: boolean;
}
export interface ApproveAllResponse {
  approved: number;
  skipped: number;
}
// Field transformation object — mirrors StandardCustomField.metadata.transformation on the backend.
// `type:"ai"` is the AI toggle; prompt + responseType drive the AI worker.
export interface FieldTransformation {
  flags?: string;
  mapping?: Record<string, string>;
  pattern?: string;
  prompt?: string;
  replacement?: string;
  responseType?: "boolean" | "date" | "number" | "string";
  type: "ai" | "date" | "map" | "regex";
}

// `POST /inbox/field-drift/approve-batch` — apply a whole reviewed set of column mappings at
// once (the wizard's "review all mappings, then approve" screen).
export interface ApproveFieldsBatchRequest {
  crmConfigId: string;
  decisions: Array<{
    sourceField: string;
    target: {
      fieldId?: string;
      kind: FieldTargetKind;
      proposedDataType?: string;
      proposedDisplayName?: string;
      proposedFormat?: string;
      proposedIsPII?: boolean;
      // Only include when AI toggle is on and a prompt is entered.
      proposedTransformation?: FieldTransformation;
      proposedType?: string;
    };
  }>;
  reproject?: boolean;
}
export interface ApproveFieldsBatchResponse {
  applied: number;
  ignored: number;
}
// §2 `POST /sources/:id/reprocess` — re-project the source's already-received data
// through its CURRENT confirmed structure. Re-extracts from the retained raw bytes,
// so a changed unique id / records path re-keys (and can create) case records.
export interface ReprocessSourceResponse {
  created: number; // cases created under a (possibly new) external id this run
  noConfirmedStructure: boolean; // true → no confirmed structure to re-project through (no-op)
  reprojected: number; // total cases re-projected
  staleByOldKey: number; // prior-key cases left behind by a re-key (never deleted)
  updated: number; // cases updated under an unchanged external id
}
// §2 `POST /inbox/create-campaign` — creates campaign with full field set; adds source org as CampaignOrg.
export interface CreateCampaignRequest {
  // Optional practice-area category (e.g. "Sexual Abuse") — maps to a UI icon.
  category?: null | string;
  crmConfigId?: string; // feeding source — stamped onto campaign.crmMetadata.crmConfigId
  // Attorney fee cap — persists to Campaign.defaultLegalFeePercent. Defaults to 40 server-side.
  defaultLegalFeePercent?: null | number;
  description?: null | string;
  // Curated mass tort; omitted or null stores null (Other). Unknown id is a 400.
  massTortId?: null | string;
  // Legacy field kept for backward compat; new callers should use `type`.
  matterType?: string;
  name: string;
  organizationId: string;
  // CampaignType value (MASS_TORT / CLASS_ACTION / MDL / INDIVIDUAL / OTHER). Takes precedence
  // over matterType when present. Defaults to MASS_TORT server-side.
  type?: string;
}
export interface CreateCampaignResponse {
  campaignId: string;
}

const ingestionApi = {
  // ---- Standard field catalog (canonical-inclusive, wizard-local) ----
  // Returns the org's real StandardCustomField rows PLUS canonical core-field stubs
  // (id = `canonical:<entityType>:<fieldKey>`) for fields the org hasn't created yet.
  // This is intentionally wizard-local — do NOT use it to populate adminStore.standardFields,
  // which is consumed by FieldManagement and must stay canonical-free.
  getStandardFieldCatalog: async (crmConfigId?: string): Promise<CatalogField[]> =>
    (
      await apiClient.get<CatalogField[]>("/crm/standard-fields", {
        params: { includeCanonical: true, ...(crmConfigId ? { crmConfigId } : {}) },
      })
    ).data,

  clearPrevToken: async (crmConfigId: string): Promise<void> => {
    await apiClient.post(`/admin/ingestion/sources/${crmConfigId}/clear-prev-token`, {});
  },
  createSource: async (body: CreateSourceRequest): Promise<CreateSourceResponse> =>
    (await apiClient.post<CreateSourceResponse>("/admin/ingestion/sources", body)).data,
  deleteSource: async (crmConfigId: string): Promise<void> => {
    await apiClient.delete(`/admin/ingestion/sources/${crmConfigId}`);
  },
  // Pre-population for the "Re-run setup" wizard over an already-confirmed source.
  getSourceEditContext: async (crmConfigId: string): Promise<SourceEditContext> =>
    (await apiClient.get<SourceEditContext>(`/admin/ingestion/sources/${crmConfigId}/edit-context`))
      .data,
  // Recent receive log for the Overview "Ingestion history" card.
  getSourceHistory: async (crmConfigId: string): Promise<IngestionHistoryItem[]> =>
    (await apiClient.get<IngestionHistoryItem[]>(`/admin/ingestion/sources/${crmConfigId}/history`))
      .data,
  // Confirmed structures for a source — the map of structureSignature → ConfirmedStructure
  // stored in config.structures, with routingField merged in from config.routingConfig.field.
  getSourceStructures: async (
    crmConfigId: string
  ): Promise<{ structures: Record<string, ConfirmedStructure> }> =>
    (
      await apiClient.get<{ structures: Record<string, ConfirmedStructure> }>(
        `/admin/ingestion/sources/${crmConfigId}/structures`
      )
    ).data,
  listPresets: async (): Promise<SourcePreset[]> =>
    (await apiClient.get<SourcePreset[]>("/admin/ingestion/presets")).data,
  // Confirmed case-type → campaign routings for one source (Data mapping tab).
  listSourceRouting: async (crmConfigId: string): Promise<ConfirmedRouting[]> =>
    (await apiClient.get<ConfirmedRouting[]>(`/admin/ingestion/sources/${crmConfigId}/routing`))
      .data,
  // ---- Source / channel setup (§2, owned by Plan 3) ----
  listSources: async (): Promise<SourceSummary[]> =>
    (await apiClient.get<SourceSummary[]>("/admin/ingestion/sources")).data,
  // Rename a source (the editable name in the Re-run setup wizard).
  renameSource: async (crmConfigId: string, name: string): Promise<void> => {
    await apiClient.patch(`/admin/ingestion/sources/${crmConfigId}`, { name });
  },
  rotateToken: async (crmConfigId: string): Promise<{ token: string }> =>
    (
      await apiClient.post<{ token: string }>(
        `/admin/ingestion/sources/${crmConfigId}/rotate-token`,
        {}
      )
    ).data,
  // Admin (session-authed) spreadsheet upload — no bearer token needed. Posts the
  // file(s) as multipart/form-data to the admin upload route.
  uploadSpreadsheet: async (
    crmConfigId: string,
    files: File[]
  ): Promise<{
    payloadId: string;
    status: "DUPLICATE" | "QUEUED";
    workbookAnalysis?: WorkbookAnalysis;
  }> => {
    const form = new FormData();
    for (const f of files) form.append("file", f, f.name);
    return (
      await apiClient.post<{
        payloadId: string;
        status: "DUPLICATE" | "QUEUED";
        workbookAnalysis?: WorkbookAnalysis;
      }>(
        `/admin/ingestion/sources/${crmConfigId}/upload`,
        form,
        // The apiClient defaults Content-Type to application/json; clear it so the
        // browser sets multipart/form-data with the correct boundary (else the
        // backend's isMultipart() check rejects the upload with a 400).
        { headers: { "Content-Type": undefined } }
      )
    ).data;
  },

  // Persist the wizard's per-sheet review (header row, data-start, include/skip) for a
  // spreadsheet source, then re-parse the retained upload so the change takes effect.
  applySheetConfig: async (
    crmConfigId: string,
    sheets: Record<string, SheetConfigInput>
  ): Promise<{ ok: boolean; reprojected: boolean }> =>
    (
      await apiClient.post<{ ok: boolean; reprojected: boolean }>(
        `/admin/ingestion/sources/${crmConfigId}/sheet-config`,
        { sheets }
      )
    ).data,

  // Opt-in AI second opinion on a spreadsheet source's per-sheet layout. Pass the campaign
  // category list so the AI can also pre-fill a best-fit category per tab. Returns [] when AI
  // is unavailable (the wizard keeps its heuristic guesses).
  suggestSheets: async (
    crmConfigId: string,
    categories: string[] = []
  ): Promise<{ sheets: SheetSuggestion[] }> =>
    (
      await apiClient.post<{ sheets: SheetSuggestion[] }>(
        `/admin/ingestion/sources/${crmConfigId}/suggest-sheets`,
        { categories }
      )
    ).data,

  // The first ~20 raw rows of one sheet + its total row count, for the row-picker modal.
  getSheetGrid: async (
    crmConfigId: string,
    sheetName: string
  ): Promise<{ grid: string[][]; totalRows: number }> =>
    (
      await apiClient.post<{ grid: string[][]; totalRows: number }>(
        `/admin/ingestion/sources/${crmConfigId}/sheet-grid`,
        { sheetName }
      )
    ).data,

  // ---- Mission-control summary (§2, owned by Plan 2) ----
  getSummary: async (): Promise<IngestionSummary> =>
    (await apiClient.get<IngestionSummary>("/admin/ingestion/summary")).data,

  getFailuresQueue: async (): Promise<FailureItem[]> =>
    (await apiClient.get<FailureItem[]>("/admin/ingestion/inbox/failures")).data,
  getFieldDriftQueue: async (): Promise<FieldDecision[]> =>
    (await apiClient.get<FieldDecision[]>("/admin/ingestion/inbox/field-drift")).data,
  getLanding: async (): Promise<InboxLanding> =>
    (await apiClient.get<InboxLanding>("/admin/ingestion/inbox/landing")).data,
  getRoutingQueue: async (): Promise<RoutingDecision[]> =>
    (await apiClient.get<RoutingDecision[]>("/admin/ingestion/inbox/routing")).data,
  // ---- Inbox: four separate queue GETs + landing rollup (§2) ----
  getStructureQueue: async (): Promise<StructureDecision[]> =>
    (await apiClient.get<StructureDecision[]>("/admin/ingestion/inbox/structure")).data,

  approveAll: async (body: ApproveAllRequest): Promise<ApproveAllResponse> =>
    (await apiClient.post<ApproveAllResponse>("/admin/ingestion/inbox/approve-all", body)).data,
  approveFieldDrift: async (body: ApproveFieldDriftRequest): Promise<void> => {
    await apiClient.post("/admin/ingestion/inbox/field-drift/approve", body);
  },
  approveFieldsBatch: async (
    body: ApproveFieldsBatchRequest
  ): Promise<ApproveFieldsBatchResponse> =>
    (
      await apiClient.post<ApproveFieldsBatchResponse>(
        "/admin/ingestion/inbox/field-drift/approve-batch",
        body
      )
    ).data,
  approveRouting: async (body: ApproveRoutingRequest): Promise<void> => {
    await apiClient.post("/admin/ingestion/inbox/routing/approve", body);
  },
  // Assign many routing values → campaigns in ONE call (multi-tab: one campaign per tab). The
  // server routes each value fast and projects the cases in the background, so this returns in
  // seconds instead of blocking per-tab while every case syncs.
  approveRoutingBatch: async (body: {
    assignments: { campaignId: string; routingValue: string }[];
    crmConfigId: string;
  }): Promise<{
    errors: { message: string; routingValue: string }[];
    ok: boolean;
    routed: number;
  }> =>
    (
      await apiClient.post<{
        errors: { message: string; routingValue: string }[];
        ok: boolean;
        routed: number;
      }>("/admin/ingestion/inbox/routing/approve-batch", body)
    ).data,
  // ---- Inbox: approvals (canonical nested bodies) ----
  approveStructure: async (body: ApproveStructureRequest): Promise<void> => {
    await apiClient.post("/admin/ingestion/inbox/structure/approve", body);
  },
  createCampaign: async (body: CreateCampaignRequest): Promise<CreateCampaignResponse> =>
    (await apiClient.post<CreateCampaignResponse>("/admin/ingestion/inbox/create-campaign", body))
      .data,
  createFirm: async (body: { name: string; orgType: FirmRole }): Promise<Firm> =>
    (await apiClient.post<Firm>("/admin/ingestion/inbox/create-firm", body)).data,
  listFirms: async (): Promise<FirmsResponse> =>
    (await apiClient.get<FirmsResponse>("/admin/ingestion/inbox/firms")).data,

  // ---- Ops (§2) ----
  reprocessPayload: async (payloadId: string): Promise<void> => {
    await apiClient.post(`/admin/ingestion/payloads/${payloadId}/reprocess`, {});
  },
  // Re-project a source's already-received data through its current confirmed
  // structure — re-reads the retained data and re-keys/re-routes existing cases.
  reprocessSource: async (crmConfigId: string): Promise<ReprocessSourceResponse> =>
    (
      await apiClient.post<ReprocessSourceResponse>(
        `/admin/ingestion/sources/${crmConfigId}/reprocess`,
        {}
      )
    ).data,
  retryRecord: async (recordId: string): Promise<void> => {
    await apiClient.post(`/admin/ingestion/records/${recordId}/retry`, {});
  },
  // 503 until Plan 3 wires `ingestionRunNow` (URL pull).
  runNow: async (crmConfigId: string): Promise<void> => {
    // The unwired 503 is shown in the wizard, not treated as an outage.
    await apiClient.post(
      `/admin/ingestion/sources/${crmConfigId}/run-now`,
      {},
      { skipMaintenanceRedirect: true }
    );
  },
};

export default ingestionApi;
